import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface EpisodeQueueItem {
  episodeId: string;
  taddyEpisodeUuid: string;
  title: string;
  artist: string;
  artwork: string | null;
  audioUrl: string | null;
  audioDurationSeconds: number | null;
}

interface EpisodeQueueContextType {
  episodeQueue: EpisodeQueueItem[];
  currentEpisodeIndex: number;
  getNextEpisode: () => EpisodeQueueItem | null;
  getPreviousEpisode: () => EpisodeQueueItem | null;
  setCurrentEpisodeIndex: (index: number) => void;
  findEpisodeIndex: (taddyEpisodeUuid: string) => number;
  refreshEpisodeQueue: () => Promise<void>;
}

const EpisodeQueueContext = createContext<EpisodeQueueContextType | null>(null);

export function useEpisodeQueue() {
  const context = useContext(EpisodeQueueContext);
  if (!context) {
    throw new Error('useEpisodeQueue must be used within EpisodeQueueProvider');
  }
  return context;
}

export function useEpisodeQueueOptional() {
  return useContext(EpisodeQueueContext);
}

export function EpisodeQueueProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const [episodeQueue, setEpisodeQueue] = useState<EpisodeQueueItem[]>([]);
  const [currentEpisodeIndex, setCurrentEpisodeIndex] = useState(-1);

  const refreshEpisodeQueue = useCallback(async () => {
    if (authLoading) return;
    
    if (!user?.id) {
      setEpisodeQueue([]);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('saved_episodes')
        .select('id, taddy_episode_uuid, episode_name, podcast_name, episode_thumbnail, episode_audio_url, episode_duration_seconds')
        .eq('user_id', user.id)
        .eq('is_completed', false)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('[EpisodeQueue] Error fetching queue:', error);
        return;
      }

      const items: EpisodeQueueItem[] = (data || [])
        .filter(ep => ep.episode_audio_url)
        .map(ep => ({
          episodeId: ep.id,
          taddyEpisodeUuid: ep.taddy_episode_uuid,
          title: ep.episode_name,
          artist: ep.podcast_name,
          artwork: ep.episode_thumbnail,
          audioUrl: ep.episode_audio_url,
          audioDurationSeconds: ep.episode_duration_seconds,
        }));

      setEpisodeQueue(items);
    } catch (err) {
      console.error('[EpisodeQueue] Failed to refresh queue:', err);
    }
  }, [authLoading, user?.id]);

  useEffect(() => {
    if (!authLoading) {
      refreshEpisodeQueue();
    }
  }, [authLoading, refreshEpisodeQueue]);

  const findEpisodeIndex = useCallback((taddyEpisodeUuid: string): number => {
    return episodeQueue.findIndex(item => item.taddyEpisodeUuid === taddyEpisodeUuid);
  }, [episodeQueue]);

  const getNextEpisode = useCallback((): EpisodeQueueItem | null => {
    if (episodeQueue.length === 0) return null;
    if (currentEpisodeIndex < 0) return episodeQueue[0] || null;
    const nextIndex = currentEpisodeIndex + 1;
    if (nextIndex < episodeQueue.length) return episodeQueue[nextIndex];
    return null;
  }, [episodeQueue, currentEpisodeIndex]);

  const getPreviousEpisode = useCallback((): EpisodeQueueItem | null => {
    if (episodeQueue.length === 0 || currentEpisodeIndex <= 0) return null;
    return episodeQueue[currentEpisodeIndex - 1] || null;
  }, [episodeQueue, currentEpisodeIndex]);

  const value: EpisodeQueueContextType = {
    episodeQueue,
    currentEpisodeIndex,
    getNextEpisode,
    getPreviousEpisode,
    setCurrentEpisodeIndex,
    findEpisodeIndex,
    refreshEpisodeQueue,
  };

  return (
    <EpisodeQueueContext.Provider value={value}>
      {children}
    </EpisodeQueueContext.Provider>
  );
}
